import React from 'react';
import {Link} from 'react-router';
import PropTypes from 'prop-types';
import {history} from 'client/history';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import sessionStore from 'client/Session/SessionStore';
import {GuideLevelModelV2} from 'resources/augmented/GuideLevel/GuideLevelModel.v2';
import constants from 'client/constants';
import {IconButton, Menu, copyToClipboard, addToast} from '@albert-io/atomic';

import {useContentDiscoveryStore} from 'client/components/ContentDiscovery/store/ContentDiscoveryStore.react';
import {getQuestionSetsByGuideLevelIdQuery} from 'resources/Question/QuestionSet.queries';
import FoldersModal from 'components/PracticeView/FoldersModal/FoldersModal.react';
import AssignModal, {SmartAssignTooltip} from 'components/AssignModal/AssignModal.react';
import FeatureFlag from 'client/components/FeatureFlag/FeatureFlag.react';
import {
  ManageRestrictionsModal,
  ADMIN_MANAGING_RESTRICTIONS_FLAG
} from 'client/components/ContentDiscovery/ManageRestrictionsModal';
import './actions-dropdown.scss';
import track from 'react-tracking';

@track()
export default class ActionsDropdown extends React.Component {
  static propTypes = {
    guideLevel: PropTypes.instanceOf(GuideLevelModelV2),
    link: PropTypes.string,
    quickAssign: PropTypes.bool,
    questionSetIds: PropTypes.array,
    questionCount: PropTypes.number,
    // Only guide-level surfaces expose the restrict action — not individual question rows.
    showManageRestrictions: PropTypes.bool,
    guideLevelTree: PropTypes.object,
    tracking: PropTypes.object
  };

  static defaultProps = {
    quickAssign: true,
    showManageRestrictions: false
  };

  constructor(props) {
    super(props);

    this.state = {
      showFolderModal: false,
      questionSetIds: props.questionSetIds || [],
      questionCount: props.questionCount || 0,
      showSmartModal: false,
      showRestrictionsModal: false
    };
  }

  toggleRestrictionsModal = () => {
    this.setState((state) => ({
      showRestrictionsModal: !state.showRestrictionsModal
    }));
  };

  trackClick = (content, options = {}) => {
    const {tracking} = this.props;
    tracking.trackEvent({
      event: 'click',
      content,
      component: 'assign dropdown',
      type: 'dropdown item',
      ...options
    });
  };

  handleQuickAssign = async () => {
    const guideLevelId = this.props.guideLevel.getId();
    const {setLegacyItemId, setLegacyItemType} = useContentDiscoveryStore.getState().draft;
    this.trackClick('Assign all');
    setLegacyItemId(guideLevelId);
    setLegacyItemType('guide');
    history.pushState(null, `/assignment-builder/create-assignment/settings`);
  };

  handleSmartAssign = () => {
    const {guideLevel} = this.props;
    const questionCount = guideLevel.getMeta().getCountOfQuestions();
    const minSizeQuestionSet = guideLevel.getMeta().getMinSizeQuestionSet();
    this.trackClick('Smart assign');

    if (questionCount === 1) {
      this.handleQuickAssign();
    } else if (questionCount === minSizeQuestionSet) {
      this.handleQuickAssign();
    } else {
      this.setState({
        showSmartModal: true
      });
    }
  };

  /**
   * Fetches question sets and sets state
   */
  fetchQuestionSets = async () => {
    if (this.state.questionSetIds.length) {
      return;
    }

    const questionSets = await getQuestionSetsByGuideLevelIdQuery(
      this.props.guideLevel.getId()
    ).getResourcePromise();
    const questionSetIds = [];
    let questionCount = 0;
    questionSets.forEach((set) => {
      questionSetIds.push(set.getId());
      questionCount += set.getQuestions().size;
    });
    this.setState({
      questionSetIds,
      questionCount
    });
  };

  toggleFoldersModal = () => {
    this.setState(
      (state) => ({
        showFolderModal: !state.showFolderModal
      }),
      () => {
        if (this.state.showFolderModal) {
          this.fetchQuestionSets();
        }
      }
    );
  };

  handleView = () => {
    const {link} = this.props;
    history.pushState(null, link);
  };

  handleCopy = () => {
    const {link} = this.props;
    copyToClipboard(`${constants.ALBERT_FULL_PATH}${link}`, () => {
      addToast({
        color: 'positive',
        title: 'Link copied!',
        message: 'Share this link.'
      });
    });
  };

  render() {
    const {guideLevel, quickAssign, link, showManageRestrictions, guideLevelTree} = this.props;
    return (
      <>
        <Menu closeOnItemClick position='bottom-end' wrapperClassName='actions-dropdown__wrapper'>
          <Menu.Trigger>
            {(triggerProps) => (
              <IconButton
                {...triggerProps}
                label='Actions'
                className='actions-dropdown__button'
                icon='ellipsis-v'
                onClick={(e) => {
                  triggerProps.onClick(e);
                  this.trackClick('Assign', {component: 'Toolbar', type: 'dropdown'});
                }}
              />
            )}
          </Menu.Trigger>
          <Menu.Content>
            {link && (
              <>
                <Menu.Item
                  as={Link}
                  to={link}
                  className='no-underline'
                  preventDefaultClickHandler={false}
                  onClick={() => this.trackClick('View')}
                >
                  View
                </Menu.Item>
                <Menu.Divider />
              </>
            )}
            {quickAssign && (
              <Menu.Item
                onClick={() => {
                  this.handleQuickAssign();
                  this.trackClick('Assign all');
                }}
              >
                {`Assign${this.state.questionCount !== 1 ? ' All' : ''}`}
              </Menu.Item>
            )}
            <Menu.Item onClick={this.handleSmartAssign}>
              <SmartAssignTooltip className='grow' leftContent='Smart assign' />
            </Menu.Item>
            <Menu.Item
              onClick={() => {
                this.toggleFoldersModal();
                this.trackClick('Save to Folders');
              }}
            >
              Save to Folders
            </Menu.Item>
            {link && (
              <Menu.Item
                onClick={() => {
                  this.handleCopy();
                  this.trackClick('Copy Link');
                }}
              >
                Copy Link
              </Menu.Item>
            )}
            {showManageRestrictions && sessionStore.isSchoolAdministrator() && (
              <FeatureFlag name={ADMIN_MANAGING_RESTRICTIONS_FLAG}>
                <Menu.Divider />
                <Menu.Item
                  onClick={() => {
                    this.toggleRestrictionsModal();
                    this.trackClick('Manage Restrictions');
                  }}
                  rightIconProps={{icon: 'lock', iconStyle: 'regular'}}
                >
                  Manage Restrictions
                </Menu.Item>
              </FeatureFlag>
            )}
          </Menu.Content>
        </Menu>
        {showManageRestrictions && this.state.showRestrictionsModal && (
          <ManageRestrictionsModal
            guideLevelName={guideLevel.getName()}
            guideLevelId={guideLevel.getId()}
            guideLevelTree={guideLevelTree}
            onClose={() => this.setState({showRestrictionsModal: false})}
          />
        )}
        {this.state.showFolderModal && (
          <FoldersModal
            selectedQuestionSetIds={this.state.questionSetIds}
            userId={masqueradeStore.getUserIdByMasqueradeState()}
            closeModalFunc={this.toggleFoldersModal}
            selectedQuestionCount={this.state.questionCount}
          />
        )}
        {this.state.showSmartModal && (
          <AssignModal
            resource={guideLevel}
            type='guide'
            onClose={() => {
              this.setState({showSmartModal: false});
            }}
          />
        )}
      </>
    );
  }
}
